import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Filter, AlertCircle } from "lucide-react";
import { useTrainers } from "@/lib/trainers";
import TrainerCard from "./TrainerCard";
import TrainerProfileModal from "./TrainerProfileModal";

/**
 * Reusable public-facing trainers section.
 * Same CMS data source (/api/cms/members) used by all pages.
 *
 * Props:
 *  - eyebrow, title, description: section header
 *  - variant: "default" (grid) | "compact" (2/3-col compact)
 *  - limit: max number of trainers to render
 *  - featuredOnly: only show trainers with featured=true
 *  - showFilters: render department filter chips (default false)
 *  - tone: "light" | "dark"  section background
 *  - columns: tailwind grid class for md/lg cols
 *  - showContact: pass through to TrainerCard
 *  - emptyText: fallback line when no trainers
 */
export default function TrainersSection({
  eyebrow = "The team",
  title = "Meet the trainers.",
  description,
  variant = "default",
  limit,
  featuredOnly = false,
  showFilters = false,
  tone = "light",
  columns = "md:grid-cols-2 lg:grid-cols-3",
  showContact = false,
  emptyText = "Trainer profiles will appear here once the team publishes them.",
  testid = "trainers-section",
  showViewAll = false,
  centerHeader = false,
  gap = "gap-6",
}) {
  const { trainers: sourced, all, loading, error, departments } = useTrainers({ featuredOnly });
  const [dept, setDept] = useState("All");
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => {
    let list = sourced;
    if (showFilters && dept !== "All")
      list = list.filter((t) => (t.department || "").toLowerCase() === dept.toLowerCase());
    if (limit) list = list.slice(0, limit);
    return list;
  }, [sourced, dept, limit, showFilters]);

  const dark = tone === "dark";
  const surface = tone === "surface";
  const chipCats = ["All", ...departments];

  return (
    <section
      className={dark ? "bg-[#050914] text-white" : surface ? "bg-[#F8FAFC]" : "bg-white"}
      data-testid={testid}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-[90px]">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className={`flex flex-wrap gap-4 mb-12 ${centerHeader ? "flex-col items-center text-center" : "items-end justify-between"}`}
        >
          <div className={centerHeader ? "max-w-2xl mx-auto" : "max-w-2xl"}>
            <div
              className={`text-xs uppercase tracking-[0.2em] font-semibold mb-3 ${
                dark ? "text-emerald-300" : "text-[#4F46E5]"
              }`}
            >
              {eyebrow}
            </div>
            <h2
              className={`font-display text-4xl sm:text-5xl tracking-tight font-semibold ${
                dark ? "text-white" : "text-[#0F172A]"
              }`}
            >
              {title}
            </h2>
            {description && (
              <p className={`mt-4 text-lg ${dark ? "text-white/70" : "text-gray-600"}`}>
                {description}
              </p>
            )}
          </div>
          {!loading && all.length > 0 && (
            <div
              className={`inline-flex items-center gap-1.5 text-xs ${
                dark ? "text-white/60" : "text-gray-500"
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              {filtered.length} of {all.length} shown
            </div>
          )}
        </motion.div>

        {showFilters && departments.length > 0 && (
          <div className="mb-10 flex flex-wrap items-center gap-2">
            <div
              className={`hidden sm:inline-flex items-center gap-1 text-xs font-medium mr-1 ${
                dark ? "text-white/60" : "text-gray-500"
              }`}
            >
              <Filter className="w-3.5 h-3.5" /> Department
            </div>
            {chipCats.map((c) => (
              <button
                key={c}
                onClick={() => setDept(c)}
                data-testid={`trainer-filter-${c}`}
                className={`text-sm px-3.5 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
                  dept === c
                    ? dark
                      ? "bg-white text-[#050914] border-white"
                      : "bg-[#0F172A] text-white border-[#0F172A]"
                    : dark
                      ? "border-white/15 text-white/80 hover:bg-white/10"
                      : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className={`grid ${columns} ${gap}`}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`rounded-2xl overflow-hidden border animate-pulse ${
                  dark ? "border-white/10 bg-white/5" : "border-gray-200 bg-white"
                }`}
              >
                <div className={`aspect-[4/3] ${dark ? "bg-white/5" : "bg-gray-100"}`} />
                <div className="p-5 space-y-3">
                  <div className={`h-4 w-3/4 rounded ${dark ? "bg-white/10" : "bg-gray-100"}`} />
                  <div className={`h-3 w-1/2 rounded ${dark ? "bg-white/10" : "bg-gray-100"}`} />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className={`rounded-3xl border border-dashed p-12 text-center ${dark ? "border-white/10 text-white/60" : "border-gray-200 text-gray-500"}`}>
            <AlertCircle className={`w-8 h-8 mx-auto mb-3 ${dark ? "text-white/30" : "text-gray-300"}`} />
            <div className="text-sm">Unable to load trainers right now.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div
            className={`rounded-3xl border border-dashed p-12 text-center ${
              dark ? "border-white/10 text-white/60" : "border-gray-200 text-gray-500"
            }`}
          >
            <Users className={`w-8 h-8 mx-auto mb-3 ${dark ? "text-white/30" : "text-gray-300"}`} />
            <div className="text-sm">{emptyText}</div>
          </div>
        ) : (
          <>
            <div className={`grid ${columns} ${gap}`}>
              {filtered.map((t, i) => (
                <TrainerCard
                  key={t.id}
                  trainer={t}
                  variant={variant}
                  index={i}
                  showContact={showContact}
                  onViewProfile={setSelected}
                />
              ))}
            </div>
            {showViewAll && limit && all.length > limit && (
              <div className="mt-10 text-center">
                <Link
                  to="/about"
                  data-testid="trainers-view-all"
                  className={`inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-full border transition-colors ${
                    dark ? "border-white/20 text-white hover:bg-white/10" : "border-gray-200 text-[#0F172A] hover:border-gray-400"
                  }`}
                >
                  View All Trainers
                </Link>
              </div>
            )}
          </>
        )}
      </div>
      <TrainerProfileModal trainer={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
